import React from 'react';
import Icon from '../../../components/AppIcon';

const NationalityBreakdownTab = ({ data, monthlyData, onDataClick, onExport }) => {
  return (
    <div className="space-y-8">
      {/* Nationality Breakdown */}
      <div className="card">
        <div className="p-6 border-b border-secondary-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
              <Icon name="Globe" size={20} className="text-primary" />
              <span>Biodata by Nationality</span>
            </h3>
            <button
              onClick={onExport}
              className="flex items-center space-x-2 px-4 py-2 bg-success text-white rounded-md hover:bg-success-700 transition-colors duration-150"
            >
              <Icon name="Download" size={16} />
              <span>Export Excel</span>
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50 border-b border-secondary-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Nationality</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Total</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Approved</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Rejected</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Pending</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-secondary-700 uppercase tracking-wider">Distribution</th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-secondary-200">
              {data.map((nationality, index) => (
                <tr 
                  key={nationality.nationality} 
                  className="hover:bg-secondary-50 cursor-pointer transition-colors duration-150"
                  onClick={() => onDataClick('nationality', nationality)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-accent-100 text-accent-800 rounded-full text-sm font-medium">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-text-primary">{nationality.nationality}</div>
                        <div className="text-sm text-secondary-600">Click to view workers</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-2xl font-bold text-primary">{nationality.total}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-success">{nationality.approved}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-error">{nationality.rejected}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-warning">{nationality.pending}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-secondary-200 rounded-full h-4">
                        <div className="flex h-4 rounded-full overflow-hidden">
                          <div 
                            className="bg-success"
                            style={{ width: `${nationality.total > 0 ? (nationality.approved / nationality.total) * 100 : 0}%` }}
                          ></div>
                          <div 
                            className="bg-error"
                            style={{ width: `${nationality.total > 0 ? (nationality.rejected / nationality.total) * 100 : 0}%` }}
                          ></div>
                          <div 
                            className="bg-warning"
                            style={{ width: `${nationality.total > 0 ? (nationality.pending / nationality.total) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-secondary-600 min-w-[3rem]">
                        {nationality.total} total
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Breakdown Matrix */}
      <div className="card">
        <div className="p-6 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
            <Icon name="Calendar" size={20} className="text-accent" />
            <span>Monthly Submission Matrix</span>
          </h3>
          <p className="text-sm text-secondary-600 mt-1">Click on any cell to view detailed worker listings</p>
        </div>
        
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full border border-secondary-200">
              <thead>
                <tr className="bg-accent-50">
                  <th className="px-4 py-3 text-left font-medium text-accent-800 border border-secondary-200">Month</th>
                  {data.slice(0, 6).map(nationality => (
                    <th key={nationality.nationality} className="px-4 py-3 text-center font-medium text-accent-800 border border-secondary-200">
                      {nationality.nationality}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-center font-medium text-accent-800 border border-secondary-200">Total</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map(month => {
                  const monthTotal = month.total;
                  return (
                    <tr key={month.month} className="hover:bg-secondary-50">
                      <td className="px-4 py-3 font-medium text-text-primary border border-secondary-200 bg-secondary-50">
                        {month.month}
                      </td>
                      {data.slice(0, 6).map(nationality => {
                        const count = month.biodata.filter(b => b.nationality === nationality.nationality).length;
                        return (
                          <td 
                            key={nationality.nationality} 
                            className="px-4 py-3 text-center border border-secondary-200 cursor-pointer hover:bg-primary-50 transition-colors duration-150"
                            onClick={() => onDataClick('month', {
                              ...month,
                              biodata: month.biodata.filter(b => b.nationality === nationality.nationality)
                            })}
                          >
                            <span className={`font-semibold ${count > 0 ? 'text-primary' : 'text-secondary-400'}`}>
                              {count}
                            </span>
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center font-bold text-accent border border-secondary-200 bg-accent-50">
                        {monthTotal}
                      </td>
                    </tr>
                  );
                })}
                {/* Grand Total Row */}
                <tr className="bg-primary-50 font-bold">
                  <td className="px-4 py-3 text-primary-800 border border-secondary-200">Grand Total</td>
                  {data.slice(0, 6).map(nationality => (
                    <td key={nationality.nationality} className="px-4 py-3 text-center text-primary-800 border border-secondary-200">
                      {nationality.total}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center text-primary-800 border border-secondary-200">
                    {data.reduce((sum, n) => sum + n.total, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Legend */}
          <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-success rounded"></div>
              <span>Approved</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-error rounded"></div>
              <span>Rejected</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-warning rounded"></div>
              <span>Pending</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NationalityBreakdownTab; 